import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { employeeService } from "../../services/employeeService";
import { useAuth } from "../../contexts/AuthContext";
import { HasPermission } from "../../components/AccessControl/ProtectedComponent";
import { getRolePrefix } from "../../utils/routeUtils";
import Modal from "../../components/UI/Modal";
import Notification from "../../components/UI/Notification";
import StatusBadge from "../../components/UI/StatusBadge";
import EmployeeForm from "../../components/Form/EmployeeForm";
import ReusableTable from "../../components/UI/ReusableTable";
import {
  Plus,
  Search,
  MoreVertical,
  Edit,
  Trash2,
  Download,
  Building2,
  Mail,
  IndianRupee,
  LayoutGrid,
  Table,
  Eye,
  X,
} from "lucide-react";

const EmployeeManagement = () => {
  const navigate = useNavigate();
  const { roles } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [openMenu, setOpenMenu] = useState(null);
  const [viewMode, setViewMode] = useState("cards"); // "cards" or "table"
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState({
    department: "All Departments",
    status: "All Status",
    profileStatus: "All",
  });
  const [employeeToDelete, setEmployeeToDelete] = useState(null);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    total: 0,
    per_page: 20,
  });

  // Fetch employees
  const fetchEmployees = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const params = {
          page,
          per_page: 20,
        };

        if (filters.department !== "All Departments") {
          params.department = filters.department;
        }
        if (filters.status !== "All Status") {
          params.status = filters.status;
        }
        let response;
        if (searchQuery) {
          response = await employeeService.searchEmployees(searchQuery, params);
        } else {
          response = await employeeService.getEmployees(params);
        }

        // API response structure: { success: true, data: { data: [...], current_page, last_page, total, per_page } }
        // employeeService.getEmployees() returns response.data from axios
        // So response = { success: true, data: { data: [...], current_page, ... } }

        let employeesData = [];
        let paginationData = {};

        if (response && response.success !== undefined) {
          // Standard API response format
          if (response.data) {
            if (Array.isArray(response.data.data)) {
              // Nested structure: response.data.data is the array
              employeesData = response.data.data;
              paginationData = {
                current_page: response.data.current_page,
                last_page: response.data.last_page,
                total: response.data.total,
                per_page: response.data.per_page,
              };
            } else if (Array.isArray(response.data)) {
              // Direct array: response.data is the array
              employeesData = response.data;
            }
          }
        } else if (response && Array.isArray(response)) {
          // Response is directly an array
          employeesData = response;
        } else if (response && response.data) {
          // Fallback: try to extract from response.data
          if (Array.isArray(response.data)) {
            employeesData = response.data;
          } else if (Array.isArray(response.data.data)) {
            employeesData = response.data.data;
            paginationData = {
              current_page: response.data.current_page,
              last_page: response.data.last_page,
              total: response.data.total,
              per_page: response.data.per_page,
            };
          }
        }

        setEmployees(employeesData);
        setPagination({
          current_page: paginationData.current_page || page,
          last_page: paginationData.last_page || 1,
          total: paginationData.total || employeesData.length,
          per_page: paginationData.per_page || 20,
        });

        // Debug: Log extracted data to verify
        if (employeesData.length > 0) {
          console.log(
            "Extracted employees:",
            employeesData.length,
            "First employee:",
            employeesData[0],
          );
        }
      } catch (error) {
        console.error("Error fetching employees:", error);
        setNotification({
          type: "error",
          message: "Failed to fetch employees",
        });
      } finally {
        setLoading(false);
      }
    },
    [filters, searchQuery],
  );

  useEffect(() => {
    fetchEmployees(1);
  }, [fetchEmployees]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openMenu && !event.target.closest(`[data-menu-id="${openMenu}"]`)) {
        setOpenMenu(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openMenu]);

  // Get initials for avatar
  const getInitials = (firstName, lastName) => {
    const first = firstName?.charAt(0)?.toUpperCase() || "";
    const last = lastName?.charAt(0)?.toUpperCase() || "";
    return first + last || "??";
  };

  // Handle form submit
  const handleFormSubmit = async (formData) => {
    try {
      setLoading(true);
      if (editingEmployee) {
        // Use employee_id as primary identifier for PUT requests
        const employeeId =
          editingEmployee.employee_id || editingEmployee.employment_id;
        if (!employeeId) {
          throw new Error("Employee ID not found");
        }
        await employeeService.updateEmployee(employeeId, formData);
        setNotification({
          type: "success",
          message: "Employee updated successfully",
        });
      } else {
        await employeeService.createEmployee(formData);
        setNotification({
          type: "success",
          message: "Employee created successfully",
        });
      }
      setIsModalOpen(false);
      setEditingEmployee(null);
      fetchEmployees(pagination.current_page);
    } catch (error) {
      setNotification({
        type: "error",
        message: error.response?.data?.message || "Failed to save employee",
      });
      // Re-throw so EmployeeForm can handle it locally if needed
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Handle delete click (shows confirmation)
  const handleDelete = useCallback((employee) => {
    setEmployeeToDelete(employee);
    setOpenMenu(null);
  }, []);

  // Perform actual deletion
  const confirmDelete = async () => {
    if (!employeeToDelete) return;

    try {
      setLoading(true);
      await employeeService.deleteEmployee(
        employeeToDelete.employee_id || employeeToDelete.employment_id,
      );
      setNotification({
        type: "success",
        message: "Employee deleted successfully",
      });
      setEmployeeToDelete(null);
      fetchEmployees(pagination.current_page);
    } catch (error) {
      setNotification({
        type: "error",
        message: "Failed to delete employee",
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle edit
  const handleEdit = useCallback((employee) => {
    setEditingEmployee(employee);
    setIsModalOpen(true);
    setOpenMenu(null);
  }, []);

  // Handle view details
  const handleViewDetails = useCallback(
    (employee) => {
      const employeeId =
        employee.employee_id || employee.employment_id || employee.id;
      if (employeeId) {
        const rolePrefix = getRolePrefix(roles);
        navigate(`${rolePrefix}/payroll/employee-management/${employeeId}`);
      }
      setOpenMenu(null);
    },
    [navigate, roles],
  );

  // Handle add new
  const handleAddNew = useCallback(() => {
    setEditingEmployee(null);
    setIsModalOpen(true);
  }, []);

  // Get unique departments and statuses from employees
  const uniqueDepartments = [
    "All Departments",
    ...new Set(
      employees
        .map((emp) => emp.department_name || emp.department)
        .filter(Boolean),
    ),
  ];
  const uniqueStatuses = [
    "All Status",
    ...new Set(employees.map((emp) => emp.status).filter(Boolean)),
  ];

  // Filter employees by profile status (client-side)
  const filteredEmployees = useMemo(() => {
    if (filters.profileStatus === "All") return employees;
    if (filters.profileStatus === "Complete")
      return employees.filter((emp) => emp.profile_complete === true);
    if (filters.profileStatus === "Incomplete")
      return employees.filter((emp) => emp.profile_complete !== true);
    return employees;
  }, [employees, filters.profileStatus]);

  // Table columns configuration
  const tableColumns = useMemo(
    () => [
      {
        label: "Employee ID",
        field: "employee_id",
        render: (row) =>
          row.employee_id || row.employment_id || row.id || "N/A",
      },
      {
        label: "Name",
        field: "name",
        render: (row) => {
          const firstName = row.first_name || "";
          const lastName = row.last_name || "";
          const fullName = `${firstName} ${lastName}`.trim();
          return fullName || "N/A";
        },
      },
      {
        label: "Email",
        field: "email",
        render: (row) => row.email || "N/A",
      },
      {
        label: "Phone",
        field: "phone",
        render: (row) => row.phone || "N/A",
      },
      {
        label: "Department",
        field: "department",
        render: (row) => row.department_name || row.department || "N/A",
      },
      {
        label: "Designation",
        field: "designation",
        render: (row) => row.designation_name || row.designation || "N/A",
      },
      {
        label: "Salary",
        field: "salary",
        render: (row) => {
          const salary =
            row.salary || row.basic_salary || row.gross_salary || 0;
          return `₹${salary.toLocaleString("en-IN")}/month`;
        },
      },
      {
        label: "Profile",
        field: "profile_complete",
        render: (row) => {
          const complete = row.profile_complete === true;
          return (
            <span
              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                complete
                  ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                  : "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400"
              }`}
            >
              {complete ? "Complete" : "Incomplete"}
            </span>
          );
        },
      },
      {
        label: "Status",
        field: "status",
        render: (row) => <StatusBadge status={row.status || "Active"} />,
      },
      {
        label: "Actions",
        field: "actions",
        render: (row) => {
          const employeeId = row.employee_id || row.employment_id || row.id;
          return (
            <div className="relative" data-menu-id={employeeId}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenMenu(openMenu === employeeId ? null : employeeId);
                }}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
              >
                <MoreVertical
                  size={18}
                  className="text-gray-600 dark:text-gray-400"
                />
              </button>
              {openMenu === employeeId && (
                <div className="absolute right-0 top-10 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10 min-w-[160px]">
                  <button
                    onClick={() => {
                      handleViewDetails(row);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                  >
                    <Eye size={16} />
                    View Details
                  </button>
                  <div className="border-t border-gray-200 dark:border-gray-700"></div>
                  <HasPermission permission="employee_mgmt_update">
                    <button
                      onClick={() => {
                        handleEdit(row);
                        setOpenMenu(null);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                    >
                      <Edit size={16} />
                      Edit
                    </button>
                    <div className="border-t border-gray-200 dark:border-gray-700"></div>
                  </HasPermission>
                  <HasPermission permission="employee_mgmt_delete">
                    <button
                      onClick={() => {
                        handleDelete(row);
                        setOpenMenu(null);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                    >
                      <Trash2 size={16} />
                      Delete
                    </button>
                  </HasPermission>
                </div>
              )}
            </div>
          );
        },
      },
    ],
    [openMenu, handleEdit, handleDelete, handleViewDetails],
  );

  return (
    <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Employee Management
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Manage employee profiles, salary components, and bank details
            </p>
          </div>
          <div className="flex items-center gap-3">
            <HasPermission permission="employee_mgmt_create">
              <button
                onClick={handleAddNew}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
              >
                <Plus size={20} />
                Add Employee
              </button>
            </HasPermission>
            <button className="flex items-center gap-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
              <Download size={20} />
              Export
            </button>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 mb-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  size={20}
                />
                <input
                  type="text"
                  placeholder="Search by name, ID, or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-10 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-900 dark:text-white"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                  >
                    <X size={18} />
                  </button>
                )}
              </div>
            </div>
            <div className="flex gap-4">
              <select
                value={filters.department}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    department: e.target.value,
                  }))
                }
                className="px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-900 dark:text-white"
              >
                {uniqueDepartments.map((dept) => (
                  <option key={dept} value={dept}>
                    {dept}
                  </option>
                ))}
              </select>
              <select
                value={filters.status}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, status: e.target.value }))
                }
                className="px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-900 dark:text-white"
              >
                {uniqueStatuses.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
              <select
                value={filters.profileStatus}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    profileStatus: e.target.value,
                  }))
                }
                className="px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-900 dark:text-white"
              >
                <option value="All">All profile status</option>
                <option value="Complete">Profile complete</option>
                <option value="Incomplete">Profile incomplete</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* View Mode Tabs */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-2 mb-4 inline-flex">
        <button
          onClick={() => setViewMode("cards")}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
            viewMode === "cards"
              ? "bg-blue-600 text-white"
              : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
          }`}
        >
          <LayoutGrid size={18} />
          Cards
        </button>
        <button
          onClick={() => setViewMode("table")}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
            viewMode === "table"
              ? "bg-blue-600 text-white"
              : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
          }`}
        >
          <Table size={18} />
          Table
        </button>
      </div>

      {/* Employee Cards Grid */}
      {viewMode === "cards" && (
        <>
          {loading && employees.length === 0 ? (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : employees.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-500 dark:text-gray-400">
                No employees found
              </p>
            </div>
          ) : filteredEmployees.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-500 dark:text-gray-400">
                No employees match the profile status filter
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                {filteredEmployees.map((employee) => {
                  const employeeId =
                    employee.employee_id || employee.employment_id;
                  const firstName = employee.first_name || "";
                  const lastName = employee.last_name || "";
                  const fullName = `${firstName} ${lastName}`.trim() || "N/A";
                  const department =
                    employee.department_name || employee.department || "N/A";
                  const email = employee.email || "N/A";
                  const salary =
                    employee.salary ||
                    employee.basic_salary ||
                    employee.gross_salary ||
                    0;
                  const role =
                    employee.designation_name || employee.designation || "N/A";
                  const status = employee.status || "Active";

                  return (
                    <div
                      key={employeeId}
                      className="bg-white dark:bg-gray-800 rounded-lg shadow-sm hover:shadow-md transition-shadow p-6 relative"
                    >
                      {/* Context Menu Button */}
                      <div
                        className="absolute top-4 right-4"
                        data-menu-id={employeeId}
                      >
                        <button
                          onClick={() =>
                            setOpenMenu(
                              openMenu === employeeId ? null : employeeId,
                            )
                          }
                          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                        >
                          <MoreVertical
                            size={18}
                            className="text-gray-600 dark:text-gray-400"
                          />
                        </button>
                        {openMenu === employeeId && (
                          <div className="absolute right-0 top-10 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10 min-w-[160px]">
                            <button
                              onClick={() => handleViewDetails(employee)}
                              className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                            >
                              <Eye size={16} />
                              View Details
                            </button>
                            <div className="border-t border-gray-200 dark:border-gray-700"></div>
                            <button
                              onClick={() => handleEdit(employee)}
                              className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                            >
                              <Edit size={16} />
                              Edit
                            </button>
                            <div className="border-t border-gray-200 dark:border-gray-700"></div>
                            <button
                              onClick={() => handleDelete(employee)}
                              className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                            >
                              <Trash2 size={16} />
                              Delete
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Avatar */}
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-blue-600 dark:text-blue-400 font-semibold text-lg">
                          {getInitials(firstName, lastName)}
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                            {fullName}
                          </h3>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {employeeId}
                          </p>
                        </div>
                      </div>

                      {/* Details */}
                      <div className="space-y-3">
                        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                          <Building2 size={16} />
                          <span>{department}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                          <Mail size={16} />
                          <span className="truncate">{email}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                          <IndianRupee size={16} />
                          <span>₹{salary.toLocaleString("en-IN")}/month</span>
                        </div>
                      </div>

                      {/* Profile complete */}
                      <div className="mt-3">
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            employee.profile_complete === true
                              ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                              : "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400"
                          }`}
                        >
                          Profile{" "}
                          {employee.profile_complete === true
                            ? "Complete"
                            : "Incomplete"}
                        </span>
                      </div>

                      {/* Footer */}
                      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {role}
                        </p>
                        <StatusBadge status={status} />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pagination */}
              {pagination.last_page > 1 && (
                <div className="flex justify-center items-center gap-2 mt-6">
                  <button
                    onClick={() => fetchEmployees(pagination.current_page - 1)}
                    disabled={pagination.current_page === 1}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    Previous
                  </button>
                  <span className="px-4 py-2 text-gray-700 dark:text-gray-300">
                    Page {pagination.current_page} of {pagination.last_page}
                  </span>
                  <button
                    onClick={() => fetchEmployees(pagination.current_page + 1)}
                    disabled={pagination.current_page === pagination.last_page}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Employee Table View */}
      {viewMode === "table" && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
          <ReusableTable
            data={filteredEmployees}
            columns={tableColumns}
            loading={loading}
            filterable={false}
          />
          {/* Pagination for table view */}
          {pagination.last_page > 1 && (
            <div className="flex justify-center items-center gap-2 p-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => fetchEmployees(pagination.current_page - 1)}
                disabled={pagination.current_page === 1}
                className="px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                Previous
              </button>
              <span className="px-4 py-2 text-gray-700 dark:text-gray-300">
                Page {pagination.current_page} of {pagination.last_page}
              </span>
              <button
                onClick={() => fetchEmployees(pagination.current_page + 1)}
                disabled={pagination.current_page === pagination.last_page}
                className="px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}

      {/* Add/Edit Employee Modal */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={() => {
          setIsModalOpen(false);
          setEditingEmployee(null);
        }}
        title={editingEmployee ? "Edit Employee" : "Add New Employee"}
        maxWidthClass="max-w-6xl"
      >
        <EmployeeForm
          initialValues={editingEmployee}
          onSubmit={handleFormSubmit}
          onCancel={() => {
            setIsModalOpen(false);
            setEditingEmployee(null);
          }}
          loading={loading}
        />
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!employeeToDelete}
        onRequestClose={() => setEmployeeToDelete(null)}
        title="Confirm Delete"
        maxWidthClass="max-w-md"
      >
        <div className="p-1">
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Are you sure you want to delete{" "}
            <span className="font-semibold text-gray-900 dark:text-white">
              {employeeToDelete?.first_name} {employeeToDelete?.last_name}
            </span>
            ? This action cannot be undone.
          </p>
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setEmployeeToDelete(null)}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={confirmDelete}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              {loading ? "Deleting..." : "Delete Employee"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default EmployeeManagement;
